import PropTypes from 'prop-types';
import React from 'react';
import bindAll from 'lodash.bindall';
import {defineMessages, intlShape, injectIntl} from 'react-intl';
import VM from '@peiyou/scratch-vm';

import AssetPanel from '../components/asset-panel/asset-panel.jsx';
import soundIcon from '../components/asset-panel/icon--sound.svg';
import soundIconRtl from '../components/asset-panel/icon--sound-rtl.svg';
import addSoundFromLibraryIcon from '../components/asset-panel/icon--add-sound-lib.svg';
import addSoundFromRecordingIcon from '../components/asset-panel/icon--add-sound-record.svg';
import fileUploadIcon from '../components/action-menu/icon--file-upload.svg';
import surpriseIcon from '../components/action-menu/icon--surprise.svg';
import searchIcon from '../components/action-menu/icon--search.svg';

import RecordModal from './record-modal.jsx';
import SoundEditor from './sound-editor.jsx';
import SoundLibrary from './sound-library.jsx';

import soundLibraryContent from '../lib/libraries/sounds.json';
import {handleFileUpload, soundUpload} from '../lib/file-uploader.js';
import errorBoundaryHOC from '../lib/error-boundary-hoc.jsx';
import DragConstants from '../lib/drag-constants';
import {uploadSoundLimitSize, uploadExceedAlert} from '../lib/upload-limit';
import {getOssSign, upload, textCheck} from '../lib/services/scratch-ser';
import axios from '../lib/services';
import swal from '../lib/swal';
import {queryParams, getUserinfo} from '../lib/services/query-params';
import {getHashParams} from '../lib/query-string';

import {connect} from 'react-redux';

import {
    closeSoundLibrary,
    openSoundLibrary,
    openSoundRecorder
} from '../reducers/modals';

import {
    activateTab,
    COSTUMES_TAB_INDEX
} from '../reducers/editor-tab';

import {setRestore} from '../reducers/restore-deletion';

const { role } = queryParams;
const workStatusQuery = getHashParams('workStatus') || queryParams.workStatus;

const getCheckStatus = result => {
    if (result && result.data && result.data.data && result.data.data.status !== undefined) {
        return result.data.data.status;
    }
    if (result && result.data && result.data.status !== undefined) {
        return result.data.status;
    }
    return null;
};

const pollCheck = async params => {
    const startTime = Date.now();
    const timeout = 300000;
    while (Date.now() - startTime < timeout) {
        const result = await axios.post(`${API_BASE_URL}/core/safeCheck/guardian/check`, params);
        const status = getCheckStatus(result);
        if (status === 3 || status === 4 || status === 5) return result;
        if (status !== 1 && status !== 2) return result;
        await new Promise(resolve => window.setTimeout(resolve, 3000));
    }
    throw new Error('guardian/check timeout');
};

const getOssMd5 = async url => {
    try {
        const res = await fetch(url, {method: 'HEAD'});
        const etag = res.headers.get('ETag');
        return etag ? etag.replace(/['"]/g, '').toLowerCase() : null;
    } catch (error) {
        return null;
    }
};

class SoundTab extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleSelectSound',
            'handleDeleteSound',
            'handleDuplicateSound',
            'handleNewSound',
            'handleSurpriseSound',
            'handleFileUploadClick',
            'handleSoundUpload',
            'handleDrop',
            'setFileInput'
        ]);
        this.state = {selectedSoundIndex: 0};
    }

    componentWillReceiveProps (nextProps) {
        const {
            editingTarget,
            sprites,
            stage
        } = nextProps;

        const target = editingTarget && sprites[editingTarget] ? sprites[editingTarget] : stage;
        if (!target || !target.sounds) {
            return;
        }

        // If switching editing targets, reset the sound index
        if (this.props.editingTarget !== editingTarget) {
            this.setState({selectedSoundIndex: 0});
        } else if (this.state.selectedSoundIndex > target.sounds.length - 1) {
            this.setState({selectedSoundIndex: Math.max(target.sounds.length - 1, 0)});
        }
    }

    handleSelectSound (soundIndex) {
        this.setState({selectedSoundIndex: soundIndex});
    }

    handleDeleteSound (soundIndex) {
        const restoreFun = this.props.vm.deleteSound(soundIndex);
        if (soundIndex >= this.state.selectedSoundIndex) {
            this.setState({selectedSoundIndex: Math.max(0, soundIndex - 1)});
        }
        this.props.dispatchUpdateRestore({restoreFun, deletedItem: 'Sound'});
    }

    handleDuplicateSound (soundIndex) {
        this.props.vm.duplicateSound(soundIndex).then(() => {
            this.setState({selectedSoundIndex: soundIndex + 1});
        });
    }

    handleNewSound () {
        if (!this.props.vm.editingTarget) {
            return null;
        }
        const sprite = this.props.vm.editingTarget.sprite;
        const sounds = sprite.sounds ? sprite.sounds : [];
        this.setState({selectedSoundIndex: Math.max(sounds.length - 1, 0)});
    }

    handleSurpriseSound () {
        const soundItem = soundLibraryContent[Math.floor(Math.random() * soundLibraryContent.length)];
        const vmSound = {
            format: soundItem.format,
            md5: soundItem.md5,
            rate: soundItem.rate,
            sampleCount: soundItem.sampleCount,
            name: soundItem.name
        };
        this.props.vm.addSound(vmSound).then(() => {
            this.handleNewSound();
        });
    }

    handleFileUploadClick () {
        this.fileInput.click();
    }

    handleSoundUpload (e) {
        const storage = this.props.vm.runtime.storage;
        const handleSound = newSound => this.props.vm.addSound(newSound)
            .then(() => this.handleNewSound());
        const fileInput = e.target;
        const uploadFile = fileInput.files && fileInput.files[0];
        if (!uploadFile) return;
        const audioFormat = uploadFile.name && uploadFile.name.lastIndexOf('.') > -1
            ? uploadFile.name.slice(uploadFile.name.lastIndexOf('.')).toLowerCase() : '';
        const actualSoundSize = (uploadFile.size / Math.pow(1024, 2)).toFixed(2);
        if (actualSoundSize >= uploadSoundLimitSize) {
            fileInput.value = null;
            uploadExceedAlert(uploadSoundLimitSize, actualSoundSize);
            return false;
        }
        const readAndUploadSound = () => {
            handleFileUpload(fileInput, (buffer, fileType, fileName) => {
                soundUpload(buffer, fileType, fileName, storage, handleSound);
            });
        };
        if (!workStatusQuery || !uploadFile.type.startsWith('audio/')) {
            readAndUploadSound();
            return;
        }
        getOssSign({
            params: [{
                type: 2,
                fileName: uploadFile.name
            }]
        })
            .then(response => {
                const signData = response.data.data[0].sign;
                const businessId = response.data.data[0].savePath || signData.fileName;
                return upload(uploadFile, signData).then(uploadRes => ({
                    signData,
                    businessId,
                    uploadRes
                }));
            })
            .then(async ({signData, businessId, uploadRes}) => {
                const {id: authorId, realName: authorName} = getUserinfo() || {};
                const url = `${signData.host.replace('-internal', '')}/${signData.dir}`;
                const fallbackMd5 = ((businessId || signData.fileName || signData.dir || '').split('?')[0].split('/').pop() || '').split('.')[0];
                const etagMd5 = uploadRes && uploadRes.headers && uploadRes.headers.etag ?
                    uploadRes.headers.etag.replace(/['"]/g, '').toLowerCase() : null;
                const md5 = etagMd5 || await getOssMd5(url) || fallbackMd5;
                const textCheckRes = await textCheck({url: uploadFile.name, module: 6});
                if (getCheckStatus(textCheckRes) === 4) {
                    await swal.alert('提示', `素材音频${audioFormat ? `(${audioFormat.replace('.', '').toUpperCase()})` : ''}不符合平台的发布规范，请重新上传`);
                    fileInput.value = null;
                    return;
                }
                const result = await pollCheck({
                    type: 2,
                    url,
                    md5,
                    module: 2,
                    authorId,
                    authorName,
                    role,
                    businessId: businessId || md5
                });
                if (getCheckStatus(result) === 4) {
                    await swal.alert('提示', `素材音频${audioFormat ? `(${audioFormat.replace('.', '').toUpperCase()})` : ''}不符合平台的发布规范，请重新上传`);
                    fileInput.value = null;
                    return;
                }
                readAndUploadSound();
            })
            .catch(error => {
                fileInput.value = null;
                console.error('sound upload check error:', error);
                swal.alert('提示', '音频资源校验失败，请稍后重试');
            });
    }

    handleDrop (dropInfo) {
        if (dropInfo.dragType === DragConstants.SOUND && dropInfo.newIndex !== null) {
            const sprite = this.props.vm.editingTarget.sprite;
            const activeSound = sprite.sounds[this.state.selectedSoundIndex];

            this.props.vm.reorderSound(this.props.vm.editingTarget.id,
                dropInfo.index, dropInfo.newIndex);

            this.setState({selectedSoundIndex: sprite.sounds.indexOf(activeSound)});
        } else if (dropInfo.dragType === DragConstants.BACKPACK_COSTUME) {
            this.props.onActivateCostumesTab();
            this.props.vm.addCostume(dropInfo.payload.body, {
                name: dropInfo.payload.name
            });
        } else if (dropInfo.dragType === DragConstants.BACKPACK_SOUND) {
            this.props.vm.addSound({
                md5: dropInfo.payload.body,
                name: dropInfo.payload.name
            }).then(this.handleNewSound);
        }
    }

    setFileInput (input) {
        this.fileInput = input;
    }

    render () {
        const {
            dispatchUpdateRestore, // eslint-disable-line no-unused-vars
            intl,
            isRtl,
            vm,
            onNewSoundFromLibraryClick,
            onNewSoundFromRecordingClick
        } = this.props;

        if (!vm.editingTarget) {
            return null;
        }

        const sprite = vm.editingTarget.sprite;

        const sounds = sprite.sounds ? sprite.sounds.map(sound => (
            {
                url: isRtl ? soundIconRtl : soundIcon,
                name: sound.name,
                details: (sound.sampleCount / sound.rate).toFixed(2),
                dragPayload: sound
            }
        )) : [];

        const messages = defineMessages({
            fileUploadSound: {
                defaultMessage: 'Upload Sound',
                description: 'Button to upload sound from file in the editor tab',
                id: 'gui.soundTab.fileUploadSound'
            },
            surpriseSound: {
                defaultMessage: 'Surprise',
                description: 'Button to get a random sound in the editor tab',
                id: 'gui.soundTab.surpriseSound'
            },
            recordSound: {
                defaultMessage: 'Record',
                description: 'Button to record a sound in the editor tab',
                id: 'gui.soundTab.recordSound'
            },
            addSound: {
                defaultMessage: 'Choose a Sound',
                description: 'Button to add a sound in the editor tab',
                id: 'gui.soundTab.addSoundFromLibrary'
            }
        });

        return (
            <AssetPanel
                buttons={[{
                    title: intl.formatMessage(messages.addSound),
                    img: addSoundFromLibraryIcon,
                    onClick: onNewSoundFromLibraryClick
                }, {
                    title: intl.formatMessage(messages.fileUploadSound),
                    img: fileUploadIcon,
                    onClick: this.handleFileUploadClick,
                    fileAccept: '.wav, .mp3',
                    fileChange: this.handleSoundUpload,
                    fileInput: this.setFileInput
                }, {
                    title: intl.formatMessage(messages.surpriseSound),
                    img: surpriseIcon,
                    onClick: this.handleSurpriseSound
                }, {
                    title: intl.formatMessage(messages.recordSound),
                    img: addSoundFromRecordingIcon,
                    onClick: onNewSoundFromRecordingClick
                }, {
                    title: intl.formatMessage(messages.addSound),
                    img: searchIcon,
                    onClick: onNewSoundFromLibraryClick
                }]}
                dragType={DragConstants.SOUND}
                isRtl={isRtl}
                items={sounds}
                selectedItemIndex={this.state.selectedSoundIndex}
                onDeleteClick={this.handleDeleteSound}
                onDrop={this.handleDrop}
                onDuplicateClick={this.handleDuplicateSound}
                onItemClick={this.handleSelectSound}
            >
                {sprite.sounds && sprite.sounds[this.state.selectedSoundIndex] ? (
                    <SoundEditor soundIndex={this.state.selectedSoundIndex} />
                ) : null}
                {this.props.soundRecorderVisible ? (
                    <RecordModal
                        onNewSound={this.handleNewSound}
                    />
                ) : null}
                {this.props.soundLibraryVisible ? (
                    <SoundLibrary
                        vm={this.props.vm}
                        onNewSound={this.handleNewSound}
                        onRequestClose={this.props.onRequestCloseSoundLibrary}
                    />
                ) : null}
            </AssetPanel>
        );
    }
}

SoundTab.propTypes = {
    dispatchUpdateRestore: PropTypes.func,
    editingTarget: PropTypes.string,
    intl: intlShape,
    isRtl: PropTypes.bool,
    onActivateCostumesTab: PropTypes.func.isRequired,
    onNewSoundFromLibraryClick: PropTypes.func.isRequired,
    onNewSoundFromRecordingClick: PropTypes.func.isRequired,
    onRequestCloseSoundLibrary: PropTypes.func.isRequired,
    soundLibraryVisible: PropTypes.bool,
    soundRecorderVisible: PropTypes.bool,
    sprites: PropTypes.shape({
        id: PropTypes.shape({
            sounds: PropTypes.arrayOf(PropTypes.shape({
                name: PropTypes.string.isRequired
            }))
        })
    }),
    stage: PropTypes.shape({
        sounds: PropTypes.arrayOf(PropTypes.shape({
            name: PropTypes.string.isRequired
        }))
    }),
    vm: PropTypes.instanceOf(VM).isRequired
};

const mapStateToProps = state => ({
    editingTarget: state.scratchGui.targets.editingTarget,
    isRtl: state.locales.isRtl,
    sprites: state.scratchGui.targets.sprites,
    stage: state.scratchGui.targets.stage,
    soundLibraryVisible: state.scratchGui.modals.soundLibrary,
    soundRecorderVisible: state.scratchGui.modals.soundRecorder
});

const mapDispatchToProps = dispatch => ({
    onActivateCostumesTab: () => dispatch(activateTab(COSTUMES_TAB_INDEX)),
    onNewSoundFromLibraryClick: e => {
        e.preventDefault();
        dispatch(openSoundLibrary());
    },
    onNewSoundFromRecordingClick: () => {
        dispatch(openSoundRecorder());
    },
    onRequestCloseSoundLibrary: () => {
        dispatch(closeSoundLibrary());
    },
    dispatchUpdateRestore: restoreState => {
        dispatch(setRestore(restoreState));
    }
});

export default errorBoundaryHOC('Sound Tab')(
    injectIntl(connect(
        mapStateToProps,
        mapDispatchToProps
    )(SoundTab))
);
